import { FaCheck, FaRedo, FaRegCopy, FaThumbsDown, FaThumbsUp } from "react-icons/fa";
import { ITextBubble } from "../interfaces/text-bubble.interface";
import { useState } from "react";
import { useChatBot } from "../../libs/use-chatbot";
import ReactMarkdown from 'react-markdown';
import { isArabicText } from "../../libs/common/helpers";
import RegenerateIcon from "../button/RegenerateIcon";
import { CustomSVGButton } from "../button/custom-svg-button";
import LikeIcon from "../button/like-icon";
import DislikeIcon from "../button/dislike-icon";

export const TextBubble = ({ messages, copyToClipboard, deleteMessage, setTyping, regenerateAnswer, darkMode }: ITextBubble) => {
    const { sendFeedback } = useChatBot();
    const [copiedMessages, setCopiedMessages] = useState<{ [key: string]: boolean }>({});
    const [feedback, setFeedback] = useState<{ [key: string]: "up" | "down" | null }>({});

    const handleCopy = (text: string, id: string) => {
        copyToClipboard(text);
        setCopiedMessages((prev) => ({ ...prev, [id]: true }));
        const timeout = setTimeout(() => {
            setCopiedMessages((prev) => ({ ...prev, [id]: false }));
            clearTimeout(timeout);
        }, 1000);
    };

    const handleRegenerateAnswer = (text: string, id: string) => {
        deleteMessage(id);
        regenerateAnswer(text);
        setTyping(true);
    };

    const handleThumbClick = (e: any, id: string, type: "up" | "down", conversationId: string | undefined, rate: number) => {
        e.preventDefault();
        if (feedback[id] === type) {
            return;
        } else {
            setFeedback((prev) => ({
                ...prev,
                [id]: prev[id] === type ? null : type,
            }));
            if (conversationId) sendFeedback(conversationId, rate);
        };
    };

    return (
        <>
            {messages.map((msg, index) => {
                const isArabic = isArabicText(msg.text);
                const justifyClass = isArabic
                    ? (msg.sender === "user" ? "justify-content-start" : "justify-content-end")
                    : (msg.sender === "user" ? "justify-content-end" : "justify-content-start");
                const isLastBotMessage = index === messages.length - 1 && msg.sender === "bot";

                return (
                    <div key={'wrap'+index} className="message-wrapper">
                    <div
                        key={index}
                        className={`d-flex ${justifyClass} mb-4 position-relative `}
                    >
                        <div
                            className={`message-container ${msg.sender === "user" ? "user-message" : darkMode ? "bot-message" : "bot-text"}`}
                            style={{
                                color: darkMode ? "white" : "#80868E",
                                backgroundColor: darkMode
                                    ? msg.sender === "user" ? "rgb(48,48,48)" : ""
                                    : msg.sender === "user" ? "#FFFFFF" : "#004B87",
                                direction: isArabic ? "rtl" : "ltr",
                            }}
                        >

                            <ReactMarkdown>{msg.text}</ReactMarkdown>

                        </div>
                        {msg.sender === "bot" && (
                            <>
                                {/*<div className="polygon"></div>*/}
                                <div
                                    className={`message-actions d-flex align-items-center gap-3 position-absolute ${isArabic ? 'flex-row-reverse' : ''}`}
                                    style={{  transform: 'translateY(-50%)', opacity: 2, transition: 'opacity 0.2s' }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => handleCopy(msg.text, msg.id)}
                                        className="copy-btn"
                                        aria-label={copiedMessages[msg.id] ? "Copied" : "Copy to clipboard"}
                                    >
                                        {copiedMessages[msg.id] ? (
                                            <svg width="28" height="28" stroke="currentColor" viewBox="0 0 28 28" fill="green" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M11 11V8.2C11 7.08 11 6.52 11.218 6.092C11.41 5.715 11.715 5.41 12.092 5.218C12.52 5 13.08 5 14.2 5H19.8C20.92 5 21.48 5 21.908 5.218C22.2843 5.40974 22.5903 5.71569 22.782 6.092C23 6.52 23 7.08 23 8.2V13.8C23 14.92 23 15.48 22.782 15.908C22.5903 16.2843 22.2843 16.5903 21.908 16.782C21.48 17 20.92 17 19.803 17H17M11 11H8.2C7.08 11 6.52 11 6.092 11.218C5.71569 11.4097 5.40974 11.7157 5.218 12.092C5 12.52 5 13.08 5 14.2V19.8C5 20.92 5 21.48 5.218 21.908C5.40974 22.2843 5.71569 22.5903 6.092 22.782C6.519 23 7.079 23 8.197 23H13.804C14.921 23 15.48 23 15.908 22.782C16.2843 22.5903 16.5903 22.2843 16.782 21.908C17 21.48 17 20.921 17 19.803V17M11 11H13.8C14.92 11 15.48 11 15.908 11.218C16.2843 11.4097 16.5903 11.7157 16.782 12.092C17 12.519 17 13.079 17 14.197V17" stroke="currentColor" stroke-width="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        ) : (
                                            <svg width="28" height="28" stroke="currentColor" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M11 11V8.2C11 7.08 11 6.52 11.218 6.092C11.41 5.715 11.715 5.41 12.092 5.218C12.52 5 13.08 5 14.2 5H19.8C20.92 5 21.48 5 21.908 5.218C22.2843 5.40974 22.5903 5.71569 22.782 6.092C23 6.52 23 7.08 23 8.2V13.8C23 14.92 23 15.48 22.782 15.908C22.5903 16.2843 22.2843 16.5903 21.908 16.782C21.48 17 20.92 17 19.803 17H17M11 11H8.2C7.08 11 6.52 11 6.092 11.218C5.71569 11.4097 5.40974 11.7157 5.218 12.092C5 12.52 5 13.08 5 14.2V19.8C5 20.92 5 21.48 5.218 21.908C5.40974 22.2843 5.71569 22.5903 6.092 22.782C6.519 23 7.079 23 8.197 23H13.804C14.921 23 15.48 23 15.908 22.782C16.2843 22.5903 16.5903 22.2843 16.782 21.908C17 21.48 17 20.921 17 19.803V17M11 11H13.8C14.92 11 15.48 11 15.908 11.218C16.2843 11.4097 16.5903 11.7157 16.782 12.092C17 12.519 17 13.079 17 14.197V17" stroke="currentColor" stroke-width="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>

                                        )}
                                    </button>
                                    {isLastBotMessage && (


                                        <CustomSVGButton
                                            hasIcon={true}
                                            hasText={true}
                                            text="Regenerate"
                                            icon={<RegenerateIcon />}
                                            onClick={() => handleRegenerateAnswer(msg.text, msg.id)}
                                            btnType="outline"
                                            btnSeverity="none"
                                            className="regen-btn"
                                        />


                                    )}
                                    <a  href="#" onClick={(e) => {  handleThumbClick(e, msg.id, "up", msg.conversationId, 1); }}
                                     className={`${feedback[msg.id] === "up" ? "like-btn-on" : "like-btn"}`} >
                                        <LikeIcon  />
                                    </a>


                                    <a href="#" onClick={(e) => handleThumbClick(e, msg.id, "down", msg.conversationId, 0)} 
                                        className={`${feedback[msg.id] === "down" ? "like-btn-on" : "like-btn"}`}>
                                         <DislikeIcon  />
                                    </a>
                                </div>
                            </>
                        )}
                    </div>
                    </div>
                );
            })}
        </>
    );
};
